import { v4 as uuidv4 } from 'uuid';
import OSS from 'ali-oss';
import config from '../../config/config.js';
import baseFunc from '../../config/base_func.js';
import apiAxios from '../../config/request.js';
import { USE_LOCAL_FILE_MOCK, createLocalFileItem, formatFileSize } from './localFile.js';
import {
	createPayFileUnlockPreviewItemsFromPages,
} from './payFileUnlock.js';
import { getQiniuSignedUrl } from './qiniuMedia.js';
import { LOCAL_ASSET } from './localAssets.js';


/** 文档上传后由后端解析为预览图；开发阶段可走本地 mock */
export const USE_MOCK_PAY_FILE_PARSE = USE_LOCAL_FILE_MOCK;

/** 后端接口：创建文件转换任务 */
export const CREATE_OFFICE_CONVERSION_TASK_API = '/file/createOfficeConversionTask';

/** 后端接口：查询文件转码任务 */
export const GET_OFFICE_CONVERSION_TASK_API = '/file/getOfficeConversionTask';

export const PAY_FILE_DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx';

const DOC_EXT = /\.(pdf|docx?|pptx?|xlsx?)$/i;
const MOCK_PAGE_COUNT = 24;

const LOCAL_PARSE_IMAGES = LOCAL_ASSET.payFileParse;

function sleep(ms) {
	return new Promise((resolve) => {
		setTimeout(resolve, ms);
	});
}

function resolveApiRequest(vm) {
	return vm?.$apiRequest || apiAxios;
}

function resolveGeditAliyunPrefix() {
	if (config.devEnv !== 'dev') return '';
	const prefix = config.geditUploadTestPrefix || 'mbm_test_gedit/';
	return String(prefix).endsWith('/') ? prefix : `${prefix}/`;
}

function buildAliyunObjectKey(file) {
	const namePart = `${baseFunc.getDateString()}${uuidv4()}${baseFunc.getType(file.name)}`;
	const prefix = resolveGeditAliyunPrefix();
	return `${prefix}file/${namePart}`;
}

function toPublicAliyunUrl(objectName) {
	const domain = String(config.aliOSS?.outDomain || '').replace(/\/$/, '');
	const path = String(objectName || '').replace(/^\//, '');
	const url = `${domain}/${path}`;
	if (url.startsWith('//')) return `https:${url}`;
	if (/^https?:\/\//i.test(url)) return url;
	return `https://${url.replace(/^\/+/, '')}`;
}

export function isPayFileDocument(file) {
	if (!file) return false;
	const name = file.name || '';
	if (DOC_EXT.test(name)) return true;
	const type = file.type || '';
	return type.includes('pdf')
		|| type.includes('word')
		|| type.includes('document')
		|| type.includes('presentation')
		|| type.includes('spreadsheet')
		|| type.includes('msword')
		|| type.includes('officedocument');
}

export function filterValidPayFileDocuments(files = [], maxCount = 1) {
	const valid = [];
	for (const file of files) {
		if (!file || !isPayFileDocument(file)) continue;
		valid.push(file);
		if (valid.length >= maxCount) break;
	}
	return valid;
}

async function simulateParseProgress(file, onProgress) {
	const steps = [10, 24, 38, 55, 72, 88, 100];
	for (const percent of steps) {
		onProgress?.({ percent, filename: file.name });
		if (percent < 100) {
			await sleep(220);
		}
	}
}

function buildMockParseResult(file, uploaded = null) {
	const fileItem = createLocalFileItem(file);
	const pages = Array.from({ length: MOCK_PAGE_COUNT }, (_, index) => ({
		id: uuidv4(),
		url: LOCAL_PARSE_IMAGES[index % LOCAL_PARSE_IMAGES.length],
		previewUrl: LOCAL_PARSE_IMAGES[index % LOCAL_PARSE_IMAGES.length],
		filename: `page-${index + 1}.jpg`,
	}));

	return {
		fileName: file.name,
		file: {
			...fileItem,
			id: uploaded?.id || fileItem.id,
			url: uploaded?.url || fileItem.url,
			path: uploaded?.path || uploaded?.url || '',
			filesize: formatFileSize(file.size),
			filesize_byte: file.size,
			local: !uploaded,
			storage: uploaded ? 'aliyun' : 'local',
		},
		items: createPayFileUnlockPreviewItemsFromPages(pages, file.name),
	};
}

function mapOfficeConversionPageItem(url, index = 0) {
	const signedPreview = baseFunc.getAliOssAuthKey(url);
	return {
		id: uuidv4(),
		url,
		previewUrl: signedPreview || url,
		filename: `page-${index + 1}.jpg`,
	};
}

function mapOfficeConversionResult(file, pageUrls = [], uploaded = null) {
	const fileName = file.name;
	const fileUrl = uploaded?.url || '';
	const fileId = uploaded?.id || 0;
	const filesizeByte = file.size;

	return {
		fileName,
		file: {
			id: fileId,
			filename: fileName,
			filesize: formatFileSize(filesizeByte),
			filesize_byte: filesizeByte,
			url: fileUrl,
			path: uploaded?.path || fileUrl,
			previewUrl: pageUrls[0] || '',
			local: false,
			storage: 'aliyun',
			party: 2,
			key: uploaded?.key || '',
		},
		items: createPayFileUnlockPreviewItemsFromPages(
			pageUrls.map(mapOfficeConversionPageItem),
			fileName,
		),
	};
}

/**
 * 单文件付费：文档上传到阿里云 OSS（独立链路，不走七牛）
 */
export async function uploadPayFileDocumentToAliyun(file, onProgress) {
	if (!config.aliOSS) {
		throw new Error('阿里云 OSS 未配置');
	}
	const client = new OSS(config.aliOSS);
	const key = buildAliyunObjectKey(file);
	const result = await client.multipartUpload(key, file, {
		progress(p) {
			onProgress?.({
				percent: Math.max(1, Math.min(70, Math.floor(Number(p) * 70))),
				filename: file.name,
			});
		},
	});
	const objectName = result?.name || key;
	const url = toPublicAliyunUrl(objectName);
	return {
		id: 0,
		key: objectName,
		url,
		path: url,
		filename: file.name,
		filesize: formatFileSize(file.size),
		filesize_byte: file.size,
		media_type: file.type || 'application/octet-stream',
	};
}

/**
 * 文件上传回调：/file/setAttachStatisticalRecord（阿里云 party=2）
 */
async function submitAliyunAttachRecord(api, file, uploaded) {
	const mediaType = file.type || uploaded.media_type || 'application/octet-stream';
	const mediaTypes = String(mediaType).split('/');
	const payload = {
		party: 2, // 1=七牛 2=阿里云 0=本地
		filesize_byte: Number(file.size || uploaded.filesize_byte || 0),
		width: 0,
		height: 0,
		duration: 0,
		media_types_a: 'file',
		key: uploaded.key,
		filename: file.name || uploaded.filename || '',
		media_type: mediaType,
		url: uploaded.url,
		path: uploaded.path || uploaded.url,
		filesize: uploaded.filesize || formatFileSize(file.size),
		type: 'file',
		media_types_b: mediaTypes[1] || '',
		file_openid: baseFunc.genID(8),
	};

	const response = await api.post('/file/setAttachStatisticalRecord', payload);
	if (response?.data?.code !== 200) {
		const err = new Error(response?.data?.message || '附件登记失败');
		err.data = response?.data;
		throw err;
	}

	const data = response.data || {};
	const attachId = data.id || data.fileId || data.data?.id || 0;
	if (!attachId) {
		const err = new Error('附件登记未返回文件ID');
		err.data = data;
		throw err;
	}
	
	return {
		...uploaded,
		id: attachId,
		fileId: attachId,
		width: Number(data.width) || 0,
		height: Number(data.height) || 0,
		duration: Number(data.duration) || 0,
		url: data.url || uploaded.url,
		path: data.path || uploaded.path || uploaded.url,
		transcode_url: data.transcode_url || '',
	};
}

async function createOfficeConversionTask(api, fileId, onProgress, filename) {
	const MAX_RETRIES = 10;
	const POLL_INTERVAL = 2000;
	
	for (let i = 0; i < MAX_RETRIES; i++) {
		onProgress?.({ percent: 82, filename, status: '正在创建文件转换任务...' });
		try {
			const response = await api.post(CREATE_OFFICE_CONVERSION_TASK_API, { id: fileId });
			
			if (!response?.data) {
				throw new Error('创建文件转换任务失败');
			}

			const code = Number(response.data.code);

			if (code === 200) {
				return;
			}

			if (code === 10001) {
				onProgress?.({ percent: 83, filename, status: '文件转换任务排队中，请稍候...' });
				await sleep(POLL_INTERVAL);
				continue;
			}

			const err = new Error(response.data.message || '创建文件转换任务失败');
			err.data = response.data;
			throw err;
		} catch (error) {
			const axiosResponse = error?.response || error;
			const errorCode = Number(axiosResponse?.data?.code);

			if (errorCode === 10001) {
				onProgress?.({ percent: 83, filename, status: '文件转换任务排队中，请稍候...' });
				await sleep(POLL_INTERVAL);
				continue;
			}

			if (error.message === '创建文件转换任务失败') {
				console.warn('创建文件转换任务异常，重试:', i + 1);
				await sleep(POLL_INTERVAL);
				continue;
			}
			console.error('创建文件转换任务失败:', error);
			throw error;
		}
	}
	
	throw new Error('创建文件转换任务超时');
}

async function pollOfficeConversionTask(api, fileId, file, onProgress) {
	const MAX_RETRIES = 60;
	const POLL_INTERVAL = 3000;

	for (let i = 0; i < MAX_RETRIES; i++) {
		const progressPercent = 85 + Math.min(12, i * 0.4);
		onProgress?.({ 
			percent: progressPercent, 
			filename: file.name, 
			status: `正在转换文档 (${i + 1})...` 
		});
		
		try {
			const response = await api.post(GET_OFFICE_CONVERSION_TASK_API, { id: fileId });
			
			if (!response?.data) {
				throw new Error('查询文件转码任务失败');
			}

			const code = Number(response.data.code);

			if (code === 200) {
				onProgress?.({ percent: 100, filename: file.name, status: '文档转换完成' });
				const pageUrls = Array.isArray(response.data.data) ? response.data.data : [];
				return pageUrls;
			}

			if (code === 9999) {
				throw new Error(response.data.message || '转换失败');
			}

			if (code === 10001) {
				onProgress?.({ 
					percent: progressPercent, 
					filename: file.name, 
					status: `文档转换中，请稍候 (${i + 1})...` 
				});
				await sleep(POLL_INTERVAL);
				continue;
			}

			if (code === 10000) {
				throw new Error(response.data.message || '文件不存在');
			}

			onProgress?.({ 
				percent: progressPercent, 
				filename: file.name, 
				status: `文档转换中，请稍候 (${i + 1})...` 
			});
			await sleep(POLL_INTERVAL);
			continue;
		} catch (error) {
			const axiosResponse = error?.response || error;
			const errorCode = Number(axiosResponse?.data?.code);
			
			if (errorCode === 10001) {
				onProgress?.({ 
					percent: progressPercent, 
					filename: file.name, 
					status: `文档转换中，请稍候 (${i + 1})...` 
				});
				await sleep(POLL_INTERVAL);
				continue;
			}

			if (errorCode === 9999) {
				throw new Error(axiosResponse.data?.message || '转换失败');
			}

			if (errorCode === 10000) {
				throw new Error(axiosResponse.data?.message || '文件不存在');
			}

			if (error.message && !['转换失败', '文件不存在', '转码任务异常'].includes(error.message)) {
				console.warn('轮询文件转码任务异常，重试:', error.message);
				await sleep(POLL_INTERVAL);
				continue;
			}
			console.error('文件转码任务失败:', error);
			throw error;
		}
	}

	throw new Error('文件转码超时');
}

/**
 * 上传文档到阿里云 → 登记附件回调 → 创建转换任务 → 轮询转换结果
 * @returns {{ fileName: string, file: object, items: array }}
 */
export async function uploadAndParsePayFileDocument(vm, file, onProgress) {
	if (!file) {
		throw new Error('请选择文档');
	}
	if (!isPayFileDocument(file)) {
		throw new Error('请上传 PDF、Word、PPT 或 Excel 文档');
	}

	if (USE_MOCK_PAY_FILE_PARSE) {
		await simulateParseProgress(file, onProgress);
		return buildMockParseResult(file);
	}

	const api = resolveApiRequest(vm);
	const uploaded = await uploadPayFileDocumentToAliyun(file, onProgress);
	onProgress?.({ percent: 78, filename: file.name });
	const recorded = await submitAliyunAttachRecord(api, file, uploaded);
	onProgress?.({ percent: 80, filename: file.name });
	onProgress?.({ percent: 81, filename: file.name, status: '正在创建文件转换任务...' });
	await createOfficeConversionTask(api, recorded.id, onProgress, file.name);
	onProgress?.({ percent: 84, filename: file.name, status: '正在等待文档转换，请稍候...' });
	const pageUrls = await pollOfficeConversionTask(api, recorded.id, file, onProgress);
	onProgress?.({ percent: 99, filename: file.name, status: '文档转换完成' });
	return mapOfficeConversionResult(file, pageUrls, recorded);
}
